import {
  Button,
  ButtonProps,
  Flex,
  Icon,
  IconButton,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalOverlay,
  SimpleGrid,
  Text,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { RiShareLine } from "@remixicon/react";
import { AiOutlineShareAlt } from "react-icons/ai";
import { useBgBaseColor, useBgHover } from "../themes/colors";
import { socMeds } from "../utils/etc";
import { ModalTitle } from "./card/ModalTitle";

interface ShareLinkModalProps extends ButtonProps {
  fontSize?: string;
  iconSize?: string;
  buttonWidth?: string;
  buttonText?: string;
  modalTitle?: string;
  modalDescription?: string;
  color?: string;
  data?: string;
}

const ShareLinkModal = ({
  fontSize = "16px",
  iconSize = "34px",
  buttonWidth = "auto",
  buttonText,
  modalTitle = "Bagikan Tautan",
  modalDescription = "Anda bisa membagikan tautan ini melalui media sosial",
  color = "secondary",
  data,
  ...props
}: ShareLinkModalProps) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();
  const bgBase = useBgBaseColor();
  const bgHover = useBgHover();

  const handleCopyLink = () => {
    const urlToCopy = data ? data : window.location.href;

    navigator.clipboard
      .writeText(urlToCopy)
      .then(() => {
        toast({
          title: `Link berhasil disalin`,
          position: "bottom",
          status: "success",
          isClosable: true,
          variant: "solid",
          containerStyle: {
            zIndex: 9999,
            backgroundColor: "green",
            color: "white",
            rounded: "4px",
          },
        });
      })
      .catch(() => {
        toast({
          title: `Link gagal disalin`,
          position: "bottom",
          status: "error",
          isClosable: true,
          variant: "solid",
          containerStyle: {
            zIndex: 9999,
            backgroundColor: "red",
            color: "white",
            rounded: "4px",
          },
        });
      });
  };

  const handleShareLink = (url: string) => {
    window.open(url, "_blank");
  };

  const handleClick = (id: number, url: string) => {
    // aim id 4 karena khusus salin link
    if (id === 4) {
      handleCopyLink();
      onClose();
      return;
    }

    let modifiedDataUrl = "";
    if (data) {
      if (id === 1) {
        modifiedDataUrl = `https://wa.me/?text=${encodeURIComponent(data)}`;
      } else if (id === 2) {
        modifiedDataUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
          data
        )}`;
      } else {
        modifiedDataUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(
          data
        )}&text=${encodeURIComponent("Check this out!")}`;
      }
    }

    handleShareLink(data ? modifiedDataUrl : url);
  };

  return (
    <>
      {buttonText ? (
        <Button
          leftIcon={<AiOutlineShareAlt size={iconSize} />}
          variant={"ghost"}
          color={color}
          fontSize={fontSize}
          onClick={onOpen}
          flex={"none"}
          w={buttonWidth}
          {...props}
        >
          {buttonText}
        </Button>
      ) : (
        <IconButton
          aria-label="Share link"
          icon={<Icon as={RiShareLine} />}
          size={"sm"}
          p={0}
          colorScheme="red"
          variant={"outline"}
          onClick={onOpen}
        />
      )}

      <Modal
        size={{ base: "xs", xs: "sm", sm: "md", md: "xl", xl: "3xl" }}
        onClose={onClose}
        isOpen={isOpen}
        isCentered
      >
        <ModalOverlay />

        <ModalContent
          rounded={{ base: "8px", xl: "16px" }}
          px={{ base: "12px", md: "40px", xl: "60px" }}
          pt={{ base: "20px", xl: "30px" }}
          w={{ base: "330px", sm: "auto" }}
        >
          <ModalCloseButton />

          <ModalBody>
            <ModalTitle title={modalTitle} subtitle={modalDescription} />

            <SimpleGrid columns={2} gap={5}>
              {socMeds.map((socMed, index) => (
                <Flex
                  aspectRatio={3 / 2}
                  as={"button"}
                  key={index}
                  direction={"column"}
                  align={"center"}
                  justify={"center"}
                  gap={"10px"}
                  bg={bgBase}
                  rounded={"16px"}
                  onClick={() => handleClick(socMed.id, socMed.shareUrl)}
                  _hover={{ bg: bgHover }}
                >
                  <Icon as={socMed.icon} boxSize={30} color={socMed.color} />

                  <Text textStyle={"h-subtitle"} fontWeight={"semibold"}>
                    {socMed.name}
                  </Text>
                </Flex>
              ))}
            </SimpleGrid>
          </ModalBody>

          <ModalFooter />
        </ModalContent>
      </Modal>
    </>
  );
};

export default ShareLinkModal;
